import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PencilIcon, PlusIcon, UserIcon } from '@heroicons/react/24/outline';

const EMPTY_FORM = {
  id: null,
  nome: '',
  documento: '',
  email: '',
  telefone: '',
  endereco_cep: '',
  endereco_logradouro: '',
  endereco_numero: '',
  endereco_complemento: '',
  endereco_bairro: '',
  endereco_cidade: '',
  endereco_estado: '',
  ativo: true,
};

const CustomersPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [cepLoading, setCepLoading] = useState(false);
  const [allowManualAddressEdit, setAllowManualAddressEdit] = useState(true);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/customers', { params: { pageSize: 200 } });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setAllowManualAddressEdit(true);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      nome: item.nome || '',
      documento: item.documento || '',
      email: item.email || '',
      telefone: item.telefone || '',
      endereco_cep: item.endereco_cep || '',
      endereco_logradouro: item.endereco_logradouro || '',
      endereco_numero: item.endereco_numero || '',
      endereco_complemento: item.endereco_complemento || '',
      endereco_bairro: item.endereco_bairro || '',
      endereco_cidade: item.endereco_cidade || '',
      endereco_estado: item.endereco_estado || '',
      ativo: item.ativo !== false,
    });
    setAllowManualAddressEdit(!(item.endereco_cep && item.endereco_logradouro));
    setShowModal(true);
  };

  const lookupCepViaCep = async (cepDigits) => {
    const { data } = await axios.get(`https://viacep.com.br/ws/${cepDigits}/json/`, {
      timeout: 8000,
    });
    if (data?.erro) return null;
    return {
      logradouro: data?.logradouro || '',
      bairro: data?.bairro || '',
      cidade: data?.localidade || '',
      estado: data?.uf || '',
      provider: 'ViaCEP',
    };
  };

  const lookupCepBrasilApi = async (cepDigits) => {
    const { data } = await axios.get(`https://brasilapi.com.br/api/cep/v1/${cepDigits}`, {
      timeout: 8000,
    });
    if (!data) return null;
    return {
      logradouro: data?.street || '',
      bairro: data?.neighborhood || '',
      cidade: data?.city || '',
      estado: data?.state || '',
      provider: 'BrasilAPI',
    };
  };

  const lookupCep = async () => {
    const cepDigits = String(form.endereco_cep || '').replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      toast.error('Informe um CEP valido com 8 digitos');
      return;
    }

    setCepLoading(true);
    try {
      let address = null;
      try {
        address = await lookupCepViaCep(cepDigits);
      } catch (_) {
        // tenta proximo provedor
      }
      if (!address) {
        address = await lookupCepBrasilApi(cepDigits);
      }
      if (!address) throw new Error('CEP nao encontrado');

      setForm((prev) => ({
        ...prev,
        endereco_cep: cepDigits,
        endereco_logradouro: address.logradouro,
        endereco_bairro: address.bairro,
        endereco_cidade: address.cidade,
        endereco_estado: address.estado,
      }));
      setAllowManualAddressEdit(false);
      toast.success(`CEP localizado via ${address.provider}. Informe apenas o numero e complemento (se houver).`);
    } catch (_) {
      setAllowManualAddressEdit(true);
      toast.error('Nao foi possivel localizar o CEP. Preencha o endereco manualmente.');
    } finally {
      setCepLoading(false);
    }
  };

  const saveCustomer = async () => {
    try {
      if (!form.nome.trim()) {
        toast.error('Nome e obrigatorio');
        return;
      }

      const payload = {
        nome: form.nome.trim(),
        documento: form.documento.trim() || null,
        email: form.email.trim() || null,
        telefone: form.telefone.trim() || null,
        endereco_cep: form.endereco_cep.replace(/\D/g, '') || null,
        endereco_logradouro: form.endereco_logradouro.trim() || null,
        endereco_numero: form.endereco_numero.trim() || null,
        endereco_complemento: form.endereco_complemento.trim() || null,
        endereco_bairro: form.endereco_bairro.trim() || null,
        endereco_cidade: form.endereco_cidade.trim() || null,
        endereco_estado: form.endereco_estado.trim().toUpperCase() || null,
        ativo: form.ativo,
      };

      if (form.id) {
        await axios.put(`/api/customers/${form.id}`, payload);
        toast.success('Cliente atualizado');
      } else {
        await axios.post('/api/customers', payload);
        toast.success('Cliente criado');
      }

      setShowModal(false);
      setForm(EMPTY_FORM);
      setAllowManualAddressEdit(true);
      await loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar cliente');
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clientes</h1>
          <p className="text-gray-600">Base de clientes da central de cobrancas</p>
        </div>
        <div className="flex items-center gap-3">
          {loading && <div className="loader h-6 w-6" />}
          <button className="btn-primary" onClick={openNew}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Novo Cliente
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Lista de Clientes ({items.length})</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Documento</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Status</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="font-medium">{item.nome}</td>
                    <td>{item.documento || '-'}</td>
                    <td>{item.email || '-'}</td>
                    <td>{item.telefone || '-'}</td>
                    <td>
                      <span className={item.ativo ? 'badge-success' : 'badge-danger'}>{item.ativo ? 'Ativo' : 'Inativo'}</span>
                    </td>
                    <td>
                      <button className="text-primary-600 hover:text-primary-900 p-1" onClick={() => openEdit(item)}>
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-gray-500">
                      <UserIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      Nenhum cliente cadastrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">{form.id ? 'Editar Cliente' : 'Novo Cliente'}</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="form-label">Nome</label>
                <input className="form-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div>
                <label className="form-label">CPF/CNPJ</label>
                <input className="form-input" value={form.documento} onChange={(e) => setForm({ ...form, documento: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Telefone</label>
                <input className="form-input" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Email</label>
                <input className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="form-label">CEP</label>
                <div className="flex gap-2">
                  <input
                    className="form-input"
                    value={form.endereco_cep}
                    onChange={(e) => setForm({ ...form, endereco_cep: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                    placeholder="Somente numeros"
                  />
                  <button type="button" className="btn-secondary whitespace-nowrap" onClick={lookupCep} disabled={cepLoading}>
                    {cepLoading ? 'Buscando...' : 'Buscar CEP'}
                  </button>
                </div>
              </div>
              <div>
                <label className="form-label">Numero</label>
                <input className="form-input" value={form.endereco_numero} onChange={(e) => setForm({ ...form, endereco_numero: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between">
                  <label className="form-label">Logradouro</label>
                  {!allowManualAddressEdit && (
                    <button type="button" className="text-xs text-primary-600 hover:text-primary-700" onClick={() => setAllowManualAddressEdit(true)}>
                      Editar manualmente
                    </button>
                  )}
                </div>
                <input
                  className="form-input"
                  value={form.endereco_logradouro}
                  onChange={(e) => setForm({ ...form, endereco_logradouro: e.target.value })}
                  disabled={!allowManualAddressEdit}
                />
              </div>
              <div>
                <label className="form-label">Complemento</label>
                <input className="form-input" value={form.endereco_complemento} onChange={(e) => setForm({ ...form, endereco_complemento: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Bairro</label>
                <input
                  className="form-input"
                  value={form.endereco_bairro}
                  onChange={(e) => setForm({ ...form, endereco_bairro: e.target.value })}
                  disabled={!allowManualAddressEdit}
                />
              </div>
              <div>
                <label className="form-label">Cidade</label>
                <input
                  className="form-input"
                  value={form.endereco_cidade}
                  onChange={(e) => setForm({ ...form, endereco_cidade: e.target.value })}
                  disabled={!allowManualAddressEdit}
                />
              </div>
              <div>
                <label className="form-label">UF</label>
                <input
                  className="form-input"
                  value={form.endereco_estado}
                  onChange={(e) => setForm({ ...form, endereco_estado: e.target.value.toUpperCase().slice(0, 2) })}
                  disabled={!allowManualAddressEdit}
                  placeholder="UF"
                />
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
                  Cliente ativo
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={saveCustomer}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;

